import { FC, useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { captureException } from '@sentry/react';
import { Col, FormInstance, Row, Upload, UploadProps } from 'antd';
import { TbPlus } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { PlatformForm as Form } from '@/shared/components/forms';
import { isProduction } from '@/shared/constants';
import { useUserStore } from '@/stores';

import { OAuthApplicationFormValues } from '../../../../../../types';

const acceptedImageTypes = ['image/jpeg', 'image/png'];
const maximumImageSize = 5 * 1024 * 1024;

interface OAuthApplicationIconFieldProps {
    form: FormInstance<OAuthApplicationFormValues>;
}

const OAuthApplicationIconField: FC<OAuthApplicationIconFieldProps> = ({ form }) => {
    const { token } = useAntDesignTheme();
    const { selectedUser } = useUserStore();
    const [uploading, setUploading] = useState(false);
    const iconUrl = Form.useWatch('iconUrl', form);
    const workspaceId = selectedUser?.activeWorkspace?.id;

    const beforeUpload: UploadProps['beforeUpload'] = (file) => {
        if (!acceptedImageTypes.includes(file.type)) {
            toast.error('Application icon must be a JPEG or PNG image.');
            return Upload.LIST_IGNORE;
        }

        if (file.size > maximumImageSize) {
            toast.error('Application icon must be smaller than 5 MB.');
            return Upload.LIST_IGNORE;
        }

        return true;
    };

    const uploadIcon: UploadProps['customRequest'] = async ({ file, onError, onSuccess }) => {
        if (!workspaceId) {
            onError?.(new Error('An active workspace is required.'));
            return;
        }

        const formData = new FormData();
        formData.append('image', file as Blob);

        try {
            setUploading(true);
            const { data } = await authorizedAxiosInstance.post<{ avatar: string }>(
                `/api/upload/${workspaceId}`,
                formData,
                {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    params: { type: 'oauth-application' },
                },
            );

            form.setFieldValue('iconUrl', data.avatar);
            void form.validateFields(['iconUrl']);
            onSuccess?.(data);
        } catch (error) {
            captureException(error);
            onError?.(error instanceof Error ? error : new Error('Application icon upload failed.'));
            toast.error('Unable to upload application icon');
        } finally {
            setUploading(false);
        }
    };

    return (
        <>
            <Form.Item label='Application Icon'>
                <Form.Item
                    name='iconUrl'
                    noStyle
                    rules={isProduction ? [{ required: true, message: 'An application icon is required' }] : undefined}
                >
                    <input type='hidden' />
                </Form.Item>
                <Row align='middle' wrap={false} gutter={12}>
                    <Col flex='none'>
                        <Upload
                            accept={acceptedImageTypes.join(',')}
                            beforeUpload={beforeUpload}
                            customRequest={uploadIcon}
                            listType='picture-card'
                            maxCount={1}
                            showUploadList={false}
                            disabled={!workspaceId || uploading}
                        >
                            {iconUrl ? (
                                <img
                                    src={iconUrl}
                                    alt='Application icon'
                                    draggable={false}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                                />
                            ) : (
                                <button
                                    id='oauth-application-field-icon'
                                    type='button'
                                    disabled={!workspaceId || uploading}
                                    style={{
                                        border: 0,
                                        background: 'none',
                                        cursor: 'pointer',
                                        color: token.colorTextTertiary,
                                    }}
                                >
                                    {uploading ? <LoadingOutlined /> : <TbPlus />}
                                    <div style={{ marginTop: '0.5rem' }}>Upload</div>
                                </button>
                            )}
                        </Upload>
                    </Col>
                    <Col flex='auto' style={{ minWidth: 0 }}>
                        <small style={{ color: token.colorTextTertiary }}>
                            Upload a JPEG or PNG image. Images are scanned and normalized before storage. 5 MB maximum.
                        </small>
                    </Col>
                </Row>
            </Form.Item>
        </>
    );
};

export default OAuthApplicationIconField;
